import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useConfirm } from "@/components/confirm-dialog";
import {
  api,
  type ServiceDraft,
  type ServiceSchedulePriceDraft,
} from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { RowAction, RowActions } from "@/components/row-actions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil } from "lucide-react";
import { messageOf, notify } from "@/lib/notify";
import { listServiceSchedules } from "@/lib/billing";
import { useAuth } from "@/lib/auth";
import { invalidateServiceLists } from "@/components/entity-select";
import { Truncated } from "@/components/truncated";
import { SearchableSelect } from "@/components/searchable-select";
import { DataExportMenu } from "@/components/export-menu";
import { exportPDFReport, exportSheetCSV, exportWorkbook } from "@/lib/export-utils";
import { pdfColumns, serviceSheets, sheetNamed, sheetRows } from "@/lib/service-export";
import { isStandardPriceList } from "@somasi/shared";
import {
  feeScheduleStandardQuery,
  invalidateStandardList,
} from "@/components/fee-schedules/queries";
import { z } from "zod";
import {
  useRowSelection,
  BulkBar,
  SelectAllCheckbox,
  RowCheckbox,
} from "@/components/bulk-actions";
import {
  FilterSelect,
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useDebounced,
  useListSort,
  usePagination,
} from "@/components/list-controls";

export const Route = createFileRoute("/_authenticated/services")({
  head: () => ({
    meta: [
      { title: "Services — Somasi Ops" },
      {
        name: "description",
        content: "Master list of services offered by Somasi Corporate Services.",
      },
    ],
  }),
  component: ServicesPage,
});

export const BILLING_FREQ = [
  "one_time",
  "monthly",
  "quarterly",
  "half_yearly",
  "annual",
  "per_transaction",
  "per_employee",
  "per_hour",
  "per_document",
  "per_company",
  "custom",
] as const;
export type BillingFrequency = (typeof BILLING_FREQ)[number];
export const FREQ_LABEL: Record<BillingFrequency, string> = {
  one_time: "One-time",
  monthly: "Monthly",
  quarterly: "Quarterly",
  half_yearly: "Half-yearly",
  annual: "Annual",
  per_transaction: "Per transaction",
  per_employee: "Per employee",
  per_hour: "Per hour",
  per_document: "Per document",
  per_company: "Per company",
  custom: "Custom",
};

export const BILLING_BASIS = [
  "fixed",
  "variable",
  "reimbursement_at_cost",
  "reimbursement_with_markup",
] as const;
export type BillingBasis = (typeof BILLING_BASIS)[number];
export const BASIS_LABEL: Record<BillingBasis, string> = {
  fixed: "Fixed fee",
  variable: "Variable fee",
  reimbursement_at_cost: "Reimbursement at cost",
  reimbursement_with_markup: "Reimbursement with markup",
};

type ServiceCategory = {
  id: string;
  code: string;
  name: string;
  active: boolean;
  sort_order: number;
};
type Service = {
  id: string;
  code: string;
  name: string;
  category_id: string | null;
  internal_description: string | null;
  invoice_description: string | null;
  default_fee: number;
  billing_frequency: BillingFrequency;
  billing_basis: BillingBasis;
  tax_treatment: string | null;
  quickbooks_item: string | null;
  payment_terms: string | null;
  supporting_docs_required: boolean;
  approval_required: boolean;
  active: boolean;
  notes: string | null;
};

const schema = z.object({
  code: z.string().trim().min(1).max(40),
  name: z.string().trim().min(1).max(200),
  category_id: z.string().optional().or(z.literal("")),
  internal_description: z.string().max(4000).optional().or(z.literal("")),
  invoice_description: z.string().max(1000).optional().or(z.literal("")),
  billing_frequency: z.enum(BILLING_FREQ),
  billing_basis: z.enum(BILLING_BASIS),
  quickbooks_item: z.string().max(120).optional().or(z.literal("")),
  active: z.boolean(),
  notes: z.string().max(4000).optional().or(z.literal("")),
});
type FormT = z.infer<typeof schema>;

function ServicesPage() {
  const { canEdit } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState<string>("all");
  const [freqFilter, setFreqFilter] = useState<string>("all");
  const [activeFilter, setActiveFilter] = useState<string>("active");
  const sort = useListSort({ column: "code", ascending: true });
  // Held back so a search is one request when typing stops, not one per key.
  const query = useDebounced(search.trim());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Service | null>(null);

  const { data: cats = [] } = useQuery({
    queryKey: ["service_categories"],
    queryFn: async () => {
      const { data, error } = await api.serviceCategories.list().order("sort_order").all();
      if (error) throw error;
      return (data ?? []) as ServiceCategory[];
    },
  });
  const catName = (id: string | null) => cats.find((c) => c.id === id)?.name ?? "—";

  /**
   * The standard price list, and what it bills today.
   *
   * One request for the whole catalogue however many services are on screen — the
   * rates are one dated version of one schedule. It is fetched rather than read off
   * each service row because the figure has to follow the schedule's own history: a
   * new version of the standard list changes every service's standard fee at once,
   * and `services.default_fee` is a stored number that the next version leaves
   * behind.
   */
  const { data: standard, error: standardError } = useQuery(feeScheduleStandardQuery());

  const standardFees = useMemo(
    () => new Map((standard?.prices ?? []).map((price) => [price.service_id, price.fee])),
    [standard],
  );

  /**
   * What this service charges as standard.
   *
   * The standard list's rate where it prices the service; the service's own stored
   * fee where it does not, which is what the price cascade falls back to and so what
   * an invoice would actually bill.
   */
  const standardFee = (service: Service) =>
    standardFees.get(service.id) ?? Number(service.default_fee ?? 0);

  const filters = { query, catFilter, freqFilter, activeFilter, sort: sort.sort };
  const hasFilters =
    Boolean(query) || catFilter !== "all" || freqFilter !== "all" || activeFilter !== "all";
  // Eight data columns, plus the checkbox column for an operator who can bulk-edit.
  const columnCount = canEdit ? 9 : 8;
  const pager = usePagination({ resetKey: filters });

  /**
   * The list the filters describe, without a window on it yet.
   *
   * One definition, two readers: the table takes a page of it and the export
   * takes all of it. Written out twice they drift, and the way that shows up is
   * an export that does not match the table it was taken from.
   */
  const filteredList = () => {
    const list = api.services.list();

    if (activeFilter !== "all") list.eq("active", activeFilter === "active");
    if (catFilter !== "all") list.eq("category_id", catFilter);
    if (freqFilter !== "all") list.eq("billing_frequency", freqFilter);
    if (query) {
      list.or([
        { column: "code", op: "ilike", value: `%${query}%` },
        { column: "name", op: "ilike", value: `%${query}%` },
        { column: "invoice_description", op: "ilike", value: `%${query}%` },
      ]);
    }

    return list.order(sort.sort.column, { ascending: sort.sort.ascending });
  };

  // Every filter, the sort and the page travel to the API, and all of them are
  // part of the cache key, so changing one is a fetch rather than a re-filter of
  // a table the browser had to download whole. The count comes back with the
  // page, which is what the footer needs — the browser cannot count rows it was
  // deliberately not sent.
  const queryKey = ["services", { ...filters, page: pager.page, pageSize: pager.pageSize }];

  const { data: result, isLoading } = useQuery({
    queryKey,
    queryFn: async (): Promise<ServiceList> => {
      const { data, error, count } = await filteredList().page(pager.page, pager.pageSize);
      if (error) throw error;
      return { rows: (data ?? []) as Service[], total: count ?? 0 };
    },
    // Keep the current page on screen while the next one loads, so the table
    // does not blank out between pages.
    placeholderData: (previous) => previous,
  });

  const data = result?.rows ?? [];
  const total = result?.total;

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (row: Service) => {
    setEditing(row);
    setDialogOpen(true);
  };
  /**
   * The table, and the pickers and name lookups elsewhere.
   *
   * `["services"]` does not reach them: React Query matches key elements, so
   * `"services_picker"` is a different key rather than a child of this one.
   */
  const refreshServices = (client: typeof qc) => {
    client.invalidateQueries({ queryKey: ["services"] });
    invalidateServiceLists(client);
  };

  /** Shows the change at once; the refetch that follows is the authority. */
  const patchCache = (rows: Service[]) =>
    qc.setQueryData<ServiceList>(queryKey, (previous) => previous && { ...previous, rows });

  /**
   * The switch in the Status column: active and inactive, one column.
   *
   * Switching off marks the service inactive rather than removing it. `DELETE
   * /services/{id}` sets `active` false and keeps the row, because every fee
   * schedule price, company plan, work log entry and issued invoice line names a
   * service by id — deleting the row would leave all of them pointing at nothing,
   * and nothing here holds a copy to put back. It stays the `DELETE` rather than a
   * patch of `active` so the audit trail still records it; switching back
   * on is the ordinary patch, since the row never went anywhere.
   *
   * Switching off still asks first: what it does to the pickers and to new invoices
   * is not readable off the switch, and the usual “this cannot be undone” is not
   * true of this one, so the question says so instead.
   */
  const setActive = async (row: Service, active: boolean) => {
    if (!active) {
      const agreed = await confirm({
        title: `Mark service “${row.name}” inactive?`,
        description:
          "It is marked inactive rather than deleted. Fee schedule prices, company " +
          "plans, work log entries and invoices already raised against it keep " +
          "working; it leaves every picker and no new invoice bills it. Switch it " +
          "active again at any time.",
        confirmLabel: "Mark inactive",
        tone: "danger",
      });
      if (!agreed) return;
    }

    patchCache(data.map((service) => (service.id === row.id ? { ...service, active } : service)));

    const { error } = active
      ? await api.services.update(row.id, { active: true })
      : await api.services.remove(row.id);
    // The refetch below puts the switch back where it was if the write failed.
    if (error) notify.error(error);
    else notify.success(`“${row.name}” is now ${active ? "active" : "inactive"}`);
    refreshServices(qc);
  };

  const sel = useRowSelection(data);

  /**
   * The catalogue and its pricing, assembled when an export is clicked.
   *
   * Two reads, and both of them have to be reads rather than anything already on
   * screen. The services are the whole filtered set — the table holds one page of
   * however many match, so exporting what is rendered hands someone a file that
   * stops silently at the bottom of the screen. The prices are the price book, which
   * is every schedule's rates in one request: the Standard Fee column on this page
   * is one schedule's rate, and "with all the pricing" is a different question that
   * no amount of the page's own state answers.
   *
   * `versions: "all"` asks for the whole dated history instead of the version in
   * force. It is a separate menu item rather than the default because it is bigger
   * by however many times every schedule has been repriced.
   */
  const exportSheets = async (versions: "in_force" | "all") => {
    const [services, book] = await Promise.all([
      filteredList()
        .all()
        .then(({ data: all, error }) => {
          if (error) throw new Error(error.message);
          return (all ?? []) as Service[];
        }),
      api.feeSchedules.priceBook({ versions }).then(({ data, error }) => {
        if (error) throw error;
        if (!data) throw new Error("The price book could not be read");
        return data;
      }),
    ]);

    return {
      book,
      sheets: serviceSheets(services, book, {
        category: catName,
        frequency: (value) => (value ? (FREQ_LABEL[value as BillingFrequency] ?? value) : ""),
        basis: (value) => BASIS_LABEL[value as BillingBasis] ?? value,
      }),
    };
  };

  /** What the file was filtered to, for the PDF's header and nothing else. */
  const filterSummary = () =>
    [
      query ? `search “${query}”` : null,
      catFilter === "all" ? null : `category ${catName(catFilter)}`,
      freqFilter === "all" ? null : `frequency ${FREQ_LABEL[freqFilter as BillingFrequency]}`,
      activeFilter === "all" ? "active and inactive" : activeFilter,
    ]
      .filter(Boolean)
      .join(", ");

  const exportGroups = [
    {
      label: "Excel workbook — every sheet",
      items: [
        {
          label: "Services with current pricing",
          hint: "Standard, a column per fee schedule, every price in force, and the services nobody prices separately.",
          run: async () => {
            const { sheets } = await exportSheets("in_force");
            exportWorkbook("somasi-services-pricing", sheets);
          },
        },
        {
          label: "Services with full pricing history",
          hint: "The same, with every dated version of every schedule rather than the one in force.",
          run: async () => {
            const { sheets } = await exportSheets("all");
            exportWorkbook("somasi-services-pricing-history", sheets);
          },
        },
      ],
    },
    {
      label: "Single table",
      items: [
        {
          label: "Services with pricing (CSV)",
          hint: "One row per service, one column per fee schedule.",
          run: async () => {
            const { sheets } = await exportSheets("in_force");
            exportSheetCSV("somasi-services", sheetNamed(sheets, "Services"));
          },
        },
        {
          label: "Fee schedule pricing (CSV)",
          hint: "One row per schedule, dated version and service, with floors, ceilings, discounts and markups.",
          run: async () => {
            const { sheets } = await exportSheets("all");
            exportSheetCSV(
              "somasi-fee-schedule-pricing",
              sheetNamed(sheets, "Fee schedule pricing"),
            );
          },
        },
        {
          label: "Pricing report (PDF)",
          hint: "The catalogue, then the schedules and what each covers.",
          run: async () => {
            const { book, sheets } = await exportSheets("in_force");
            exportPDFReport({
              filename: "somasi-services-pricing",
              title: "Service Master and Fee Schedule Pricing",
              subtitle: `Rates in force on ${book.as_of}, across ${book.schedules.length} fee schedule${
                book.schedules.length === 1 ? "" : "s"
              }`,
              filters: filterSummary(),
              sections: [
                {
                  heading: "Services",
                  // A page is not wide enough for a column per fee schedule, so
                  // the catalogue prints as a catalogue and the rates are the
                  // sections below. Said out loud, because a list of services
                  // with no money on it otherwise reads as a missing column.
                  note: "What each schedule charges is in the workbook export; the rates in force are summarised below.",
                  columns: pdfColumns([
                    "Code",
                    "Name",
                    "Category",
                    "Billing frequency",
                    "Billing basis",
                    "Active",
                  ]),
                  rows: sheetRows(sheets, "Services"),
                },
                {
                  heading: "Fee schedules",
                  pageBreak: true,
                  columns: pdfColumns([
                    "Fee schedule",
                    "Standard list",
                    "Active",
                    "Client groups",
                    "In force from",
                    "Services priced",
                    "Services unpriced",
                    "Lowest fee",
                    "Highest fee",
                  ]),
                  rows: sheetRows(sheets, "Fee schedules"),
                },
                {
                  heading: "Not separately priced",
                  note: "Every client pays the standard fee for these, whatever was negotiated.",
                  pageBreak: true,
                  columns: pdfColumns([
                    "Code",
                    "Name",
                    "Category",
                    "Standard fee",
                    "Billing frequency",
                    "Active",
                  ]),
                  rows: sheetRows(sheets, "Not separately priced"),
                },
              ],
            });
          },
        },
      ],
    },
  ];

  return (
    <ListPage>
      <ListPageHeader>
        <PageHeader
          title="Services"
          description="The Somasi service master — used to build fee schedules and company service plans."
          actions={
            <>
              <DataExportMenu groups={exportGroups} />
              {canEdit && (
                <Button onClick={openNew}>
                  <Plus className="mr-2 h-4 w-4" /> New service
                </Button>
              )}
            </>
          }
        />

        <ListToolbar>
          <ListSearch
            value={search}
            onChange={setSearch}
            placeholder="Search code, name, invoice description…"
          />
          <SearchableSelect
            value={catFilter}
            onChange={setCatFilter}
            options={[
              { value: "all", label: "All categories" },
              ...cats.map((c) => ({ value: c.id, label: c.name })),
            ]}
            searchPlaceholder="Search categories…"
            emptyText="No category matches that."
            className="w-56"
            contentClassName="w-72 p-0"
          />
          <FilterSelect
            value={freqFilter}
            onChange={setFreqFilter}
            options={[
              { value: "all", label: "All frequencies" },
              ...BILLING_FREQ.map((f) => ({ value: f, label: FREQ_LABEL[f] })),
            ]}
          />
          <FilterSelect
            value={activeFilter}
            onChange={setActiveFilter}
            className="w-36"
            options={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
              { value: "all", label: "All" },
            ]}
          />
        </ListToolbar>

        {/*
         * Where the standard fee comes from. Worth a line: the column used to be a
         * field on the service, so a reader who remembers that would otherwise have
         * no way to tell why it moved when a new version of the list started.
         */}
        <p className="mb-3 text-xs text-muted-foreground">
          {standardError
            ? `Standard fee could not be read from the standard price list (${messageOf(standardError)}), so each service shows its own stored fee.`
            : standard
              ? standard.effective_from
                ? `Standard fee is ${standard.name}'s rate in force today, dated ${standard.effective_from}. A service that list does not price shows its own stored fee.`
                : `${standard.name} is the standard price list but holds no dated prices yet, so every service shows its own stored fee.`
              : "No schedule is marked as the standard price list, so each service shows its own stored fee. Mark one on its page to price the catalogue from a schedule."}
        </p>

        {canEdit && (
          <BulkBar
            sel={sel}
            resource={api.services}
            entityLabel="service"
            activeColumn="active"
            softDelete
            onDone={() => refreshServices(qc)}
          />
        )}
      </ListPageHeader>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              {canEdit && (
                <TableHead className="w-10">
                  <SelectAllCheckbox sel={sel} />
                </TableHead>
              )}
              <SortHeader sort={sort} column="code" className="w-28">
                Code
              </SortHeader>
              <SortHeader sort={sort} column="name">
                Name
              </SortHeader>
              <TableHead>Category</TableHead>
              {/*
               * Not sortable. The figure comes from the standard list's current
               * version, not from a column on `services`, so ordering by
               * `default_fee` would sort the table by a number it is not showing.
               */}
              <TableHead className="w-32 text-right">Standard fee</TableHead>
              <SortHeader sort={sort} column="billing_frequency">
                Frequency
              </SortHeader>
              <TableHead>Basis</TableHead>
              <SortHeader sort={sort} column="active">
                Status
              </SortHeader>
              <TableHead className="w-px whitespace-nowrap text-right">
                {canEdit && "Actions"}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableMessageRow colSpan={columnCount}>Loading…</TableMessageRow>
            ) : data.length === 0 ? (
              <TableMessageRow colSpan={columnCount}>
                {hasFilters ? "No services match these filters." : "No services yet."}
              </TableMessageRow>
            ) : (
              data.map((s) => (
                <TableRow key={s.id} data-state={sel.isSelected(s.id) ? "selected" : undefined}>
                  {canEdit && (
                    <TableCell>
                      <RowCheckbox sel={sel} id={s.id} />
                    </TableCell>
                  )}
                  <TableCell className="font-mono text-xs">
                    {/* Through to what every schedule charges for this one service. */}
                    <Link
                      to="/services/$serviceId"
                      params={{ serviceId: s.id }}
                      className="underline underline-offset-2 hover:text-foreground"
                    >
                      {s.code}
                    </Link>
                  </TableCell>
                  {/* Capped, because service names run long — "Professional
                      Filing Fee — Register of Beneficial Ownership and Register of
                      Members" is one of them — and an uncapped column sizes itself
                      to the worst of them and squeezes every other column. */}
                  <TableCell className="max-w-xs">
                    <Truncated text={s.name} className="font-medium" />
                    {s.invoice_description && (
                      <Truncated
                        text={s.invoice_description}
                        className="text-xs text-muted-foreground"
                      />
                    )}
                  </TableCell>
                  <TableCell>{catName(s.category_id)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    USD {standardFee(s).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell>{FREQ_LABEL[s.billing_frequency]}</TableCell>
                  <TableCell className="text-xs">{BASIS_LABEL[s.billing_basis]}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={s.active}
                        disabled={!canEdit}
                        aria-label={`Mark ${s.active ? "inactive" : "active"}: ${s.name}`}
                        onCheckedChange={(value) => setActive(s, value)}
                      />
                      <span className="text-xs text-muted-foreground">
                        {s.active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <RowActions>
                        {/* Marking active and inactive is the switch in the Status
                            column, so they are not repeated here. */}
                        <RowAction label="Edit" onClick={() => openEdit(s)}>
                          <Pencil />
                        </RowAction>
                      </RowActions>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination pagination={pager} total={total} loading={isLoading} unit="service" />
      </ListPageTable>

      <ServiceDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        cats={cats}
        onSaved={() => {
          refreshServices(qc);
          // The fee boxes wrote prices, so the “who prices this” views are stale too,
          // and so is the standard fee this table shows.
          qc.invalidateQueries({ queryKey: ["service_fee_schedules"] });
          invalidateStandardList(qc);
        }}
      />
    </ListPage>
  );
}

type ServiceList = { rows: Service[]; total: number };

type ScheduleLite = { id: string; name: string; active: boolean; is_standard: boolean };

function ServiceDialog({
  open,
  onOpenChange,
  editing,
  cats,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: Service | null;
  cats: ServiceCategory[];
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);

  /**
   * The schedules a fee can be entered against.
   *
   * The ones switched on, because a switched-off schedule prices nothing and a rate
   * against it would collect a number that bills nobody — plus the standard price
   * list whatever its state, since its rate *is* the catalogue's own fee and that
   * still applies to every company no schedule prices.
   *
   * Standard list first: it is the rate most services need, and the one the
   * catalogue fee is taken from.
   */
  const {
    data: schedules = [],
    error: schedulesError,
    isLoading: schedulesLoading,
  } = useQuery({
    queryKey: ["fee_schedules_lite"],
    enabled: open,
    queryFn: async () => {
      // No `select`. The whole row is a handful of fields and the list is short, and
      // naming columns couples this dialog to the deployed API's column list: asking
      // for `is_standard` against a Lambda that predates it is a 400, which left the
      // fee boxes silently absent rather than saying the API was behind.
      const { data, error } = await api.feeSchedules.list().all().order("name");
      if (error) throw error;
      return ((data ?? []) as ScheduleLite[])
        .filter((schedule) => schedule.active || isStandardPriceList(schedule))
        .sort((a, b) => Number(isStandardPriceList(b)) - Number(isStandardPriceList(a)));
    },
  });

  const standard = useMemo(() => schedules.find(isStandardPriceList) ?? null, [schedules]);

  // What each schedule charges for this service today, which is what the boxes
  // start at when an existing service is opened.
  const { data: priced = [] } = useQuery({
    queryKey: ["service_fee_schedules", editing?.id],
    enabled: open && !!editing,
    queryFn: () => listServiceSchedules(editing!.id),
  });

  /**
   * Only what the person typed is held in state; everything else is read through
   * from the query.
   *
   * Keeping the stored rates out of the form state is what makes a background
   * refetch harmless — seeding them in would let one land mid-edit and overwrite
   * a half-typed fee with the number it is replacing.
   */
  const [fees, setFees] = useState<Record<string, string>>({});
  const stored = useMemo(() => {
    const out: Record<string, string> = {};
    for (const row of priced) out[row.fee_schedule_id] = String(row.fee);
    // The standard list's box is the catalogue fee, so it starts at the catalogue's
    // own number even on a service that schedule has not priced — otherwise the box
    // would read empty while the service says it charges something.
    if (standard && out[standard.id] === undefined && editing) {
      out[standard.id] = String(editing.default_fee ?? 0);
    }
    return out;
  }, [priced, standard, editing]);
  const feeOf = (scheduleId: string) => fees[scheduleId] ?? stored[scheduleId] ?? "";

  /**
   * The boxes that were changed to a number, which is exactly what gets written.
   *
   * A box left alone sends nothing — otherwise saving an unrelated edit would
   * stamp today's rate onto every dated version of every schedule, flattening the
   * history each of those versions exists to record. A box cleared sends nothing
   * either: unpricing a service is a delete on the version that holds it, not an
   * empty field here.
   */
  const changed = useMemo(
    () =>
      schedules
        .map((schedule) => ({ schedule, typed: fees[schedule.id] }))
        .filter(
          ({ schedule, typed }) =>
            typed !== undefined && typed.trim() !== "" && typed !== (stored[schedule.id] ?? ""),
        ),
    [schedules, fees, stored],
  );

  const [form, setForm] = useState<FormT>({
    code: "",
    name: "",
    category_id: "",
    internal_description: "",
    invoice_description: "",
    billing_frequency: "annual",
    billing_basis: "fixed",
    quickbooks_item: "",
    active: true,
    notes: "",
  });

  useEffect(() => {
    if (!open) return;
    setFees({});
    setForm({
      code: editing?.code ?? "",
      name: editing?.name ?? "",
      category_id: editing?.category_id ?? "",
      internal_description: editing?.internal_description ?? "",
      invoice_description: editing?.invoice_description ?? "",
      billing_frequency: editing?.billing_frequency ?? "annual",
      billing_basis: editing?.billing_basis ?? "fixed",
      quickbooks_item: editing?.quickbooks_item ?? "",
      active: editing?.active ?? true,
      notes: editing?.notes ?? "",
    });
  }, [open, editing]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) return notify.error(parsed.error.issues[0].message);

    // Checked before the service is written, so a mistyped fee is a question
    // rather than a saved service with half its prices missing.
    const prices: ServiceSchedulePriceDraft[] = [];
    for (const { schedule, typed } of changed) {
      const fee = Number(typed);
      if (!Number.isFinite(fee) || fee < 0) {
        return notify.error(`The fee for “${schedule.name}” must be a number of zero or more`);
      }
      prices.push({ fee_schedule_id: schedule.id, standard_fee: fee });
    }

    setSaving(true);
    const payload: Record<string, unknown> = { ...parsed.data };
    for (const k of Object.keys(payload)) if (payload[k] === "") payload[k] = null;

    const { data: saved, error } = editing
      ? await api.services.update(editing.id, payload as ServiceDraft)
      : await api.services.create(payload as ServiceDraft);
    if (error) {
      setSaving(false);
      return notify.error(error);
    }

    const serviceId = editing?.id ?? saved?.id;
    let priceError: { message: string } | null = null;
    let versions = 0;
    if (prices.length && serviceId) {
      const result = await api.services.priceInSchedules(serviceId, prices);
      priceError = result.error;
      versions = (result.data ?? []).reduce((sum, row) => sum + row.versions, 0);
    }
    setSaving(false);

    // The service is saved either way, so the dialog closes and the list refreshes
    // even when the prices did not land. Reopening it to retry would be a create
    // that collides with the code it just reserved; the fees go in from here again
    // on the next edit, or from the schedule's own version page.
    if (priceError) {
      notify.warning(editing ? "Service updated" : "Service created", {
        description: `Its fee schedule prices were not written: ${priceError.message}`,
      });
    } else if (prices.length) {
      notify.success(
        `${editing ? "Service updated" : "Service created"} and priced in ${prices.length} ` +
          `${prices.length === 1 ? "schedule" : "schedules"} across ${versions} dated ` +
          `${versions === 1 ? "version" : "versions"}`,
      );
    } else {
      notify.success(editing ? "Service updated" : "Service created");
    }

    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit service" : "New service"}</DialogTitle>
          <DialogDescription>
            Standard details used across price lists and company plans.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label required>Code</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                required
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label required>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select
              value={form.category_id || "__none"}
              onValueChange={(v) => setForm({ ...form, category_id: v === "__none" ? "" : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">(none)</SelectItem>
                {cats.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Standard invoice description</Label>
            <Textarea
              rows={2}
              value={form.invoice_description}
              onChange={(e) => setForm({ ...form, invoice_description: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              This is used as the base text on invoices. A period suffix (e.g. year/month) can be
              appended and the whole line edited when the invoice is created.
            </p>
          </div>
          {/*
           * No "standard Somasi fee" here. That number is the standard price list's
           * rate for this service — the schedule carrying `is_standard` — so it is
           * asked for once, in the fee rows below, and the catalogue fee the cascade
           * falls back to is written from the same box.
           */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Frequency</Label>
              <Select
                value={form.billing_frequency}
                onValueChange={(v) =>
                  setForm({ ...form, billing_frequency: v as BillingFrequency })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BILLING_FREQ.map((f) => (
                    <SelectItem key={f} value={f}>
                      {FREQ_LABEL[f]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Basis</Label>
              <Select
                value={form.billing_basis}
                onValueChange={(v) => setForm({ ...form, billing_basis: v as BillingBasis })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BILLING_BASIS.map((b) => (
                    <SelectItem key={b} value={b}>
                      {BASIS_LABEL[b]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>QuickBooks item</Label>
              <Input
                value={form.quickbooks_item}
                onChange={(e) => setForm({ ...form, quickbooks_item: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.active ? "active" : "inactive"}
                onValueChange={(v) => setForm({ ...form, active: v === "active" })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {/*
           * The rates the price lists will bill, asked for here because this is
           * where the service exists.
           *
           * Without them a new service bills every client its catalogue fee, and
           * correcting that afterwards is a visit to each schedule's version editor.
           * A fee entered here is written to every dated version the schedule holds,
           * not only the newest: the schedule never priced this service on any of
           * those dates, so there is no earlier rate to keep — only a gap that would
           * bill the catalogue fee for every past period.
           */}
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex items-baseline justify-between gap-3">
              <Label>Fees</Label>
              <span className="text-xs text-muted-foreground">
                {schedulesLoading
                  ? "Loading…"
                  : schedulesError
                    ? "Could not load"
                    : schedules.length === 0
                      ? "No fee schedules"
                      : `${schedules.length} ${schedules.length === 1 ? "schedule" : "schedules"}`}
              </span>
            </div>
            {/*
             * Said out loud. An empty block reads as "there are no fee schedules",
             * which is a different thing from "the schedules could not be fetched" —
             * and the second one is the one that needs acting on.
             */}
            {schedulesError && (
              <p className="text-xs text-destructive">
                The fee schedules could not be loaded, so no fee can be entered here:{" "}
                {messageOf(schedulesError)}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              A fee entered here becomes that schedule's standard fee on{" "}
              <strong>every dated version</strong> it holds. Left blank, the schedule does not price
              this service and the companies reading it fall back to the standard fee. Clearing a
              box changes nothing — a price is removed from the schedule's own version page.
            </p>
            {editing && (
              <p className="text-xs text-muted-foreground">
                The boxes show what each schedule charges today. Changing one rewrites that rate on
                every dated version, including the ones invoices have already been raised against.
              </p>
            )}
            {schedules.length > 0 && (
              <div className="max-h-56 space-y-2 overflow-y-auto pt-1">
                {schedules.map((schedule) => {
                  const isStandard = schedule.id === standard?.id;
                  return (
                    <div key={schedule.id} className="flex items-center gap-3">
                      <span className="flex-1 truncate text-sm">
                        {schedule.name}
                        {/*
                         * Named for what it also does. This box is the service's
                         * catalogue fee as well — the two were separate inputs for
                         * the same number, and either could be left disagreeing
                         * with the other.
                         */}
                        {isStandard && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            standard price list — this is the service's standard fee
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-muted-foreground">USD</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        className="w-32"
                        placeholder="—"
                        value={feeOf(schedule.id)}
                        onChange={(e) => setFees({ ...fees, [schedule.id]: e.target.value })}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            {/*
             * Nothing flagged means nothing is the catalogue, so there is no box to
             * fall back to and none is drawn: a service priced by no schedule has no
             * standard fee, which is the same thing the catalogue used to say with a
             * zero. Flagging a schedule is a switch on its own page.
             */}
            {!standard && schedules.length > 0 && (
              <p className="border-t pt-3 text-xs text-muted-foreground">
                No schedule is marked as the standard price list, so none of these is this service's
                standard fee. Mark one on its own page.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Internal notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Create service"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
